import { useMutation } from "@tanstack/react-query";

import {
	activateBrakes,
	activateCreaseWheel,
	activateCrossheadCreaseWheel,
	activateCrossheadKnife,
	activateCrossheadLatch,
	activateKnife,
	activateLatch,
	activatePressureRoller,
	cycleAllCrossheadTools,
	cycleAllLongheadTools,
	cycleAllTrackTools,
	deactivateBrakes,
	deactivateCrossheadLatch,
	deactivateLatch,
	deactivatePressureRoller,
	raiseCreaseWheel,
	raiseCrossheadCreaseWheel,
	raiseCrossheadKnife,
	raiseKnife,
} from "@/api/machines/em-api";

interface EmServiceCommand {
	readonly machineId: string;
	readonly command:
		| "toggle-long-head-crease"
		| "toggle-long-head-latch"
		| "toggle-long-head-cut"
		| "toggle-track-brake"
		| "toggle-track-roller"
		| "toggle-cross-head-crease"
		| "toggle-cross-head-latch"
		| "toggle-cross-head-cut"
		| "cycle-cross-head-tools"
		| "cycle-long-head-tools"
		| "cycle-track-tools";
	readonly toolNumber?: number;
	readonly isActive?: boolean;
}

const executeEmServiceCommand = async ({
	machineId,
	command,
	toolNumber,
	isActive,
}: EmServiceCommand): Promise<void> => {
	switch (command) {
		case "toggle-long-head-crease":
			if (toolNumber === undefined) {
				throw new Error("A long head number is required to toggle the crease wheel");
			}
			await (isActive === true ? raiseCreaseWheel(machineId, toolNumber) : activateCreaseWheel(machineId, toolNumber));
			return;
		case "toggle-long-head-latch":
			if (toolNumber === undefined) {
				throw new Error("A long head number is required to toggle the latch");
			}
			await (isActive === true ? deactivateLatch(machineId, toolNumber) : activateLatch(machineId, toolNumber));
			return;
		case "toggle-long-head-cut":
			if (toolNumber === undefined) {
				throw new Error("A long head number is required to toggle the knife");
			}
			await (isActive === true ? raiseKnife(machineId, toolNumber) : activateKnife(machineId, toolNumber));
			return;
		case "toggle-track-brake":
			if (toolNumber === undefined) {
				throw new Error("A track number is required to toggle the brake");
			}
			await (isActive === true ? deactivateBrakes(machineId, toolNumber) : activateBrakes(machineId, toolNumber));
			return;
		case "toggle-track-roller":
			if (toolNumber === undefined) {
				throw new Error("A track number is required to toggle the pressure roller");
			}
			await (isActive === true
				? deactivatePressureRoller(machineId, toolNumber)
				: activatePressureRoller(machineId, toolNumber));
			return;
		case "toggle-cross-head-crease":
			await (isActive === true ? raiseCrossheadCreaseWheel(machineId) : activateCrossheadCreaseWheel(machineId));
			return;
		case "toggle-cross-head-latch":
			await (isActive === true ? deactivateCrossheadLatch(machineId) : activateCrossheadLatch(machineId));
			return;
		case "toggle-cross-head-cut":
			await (isActive === true ? raiseCrossheadKnife(machineId) : activateCrossheadKnife(machineId));
			return;
		case "cycle-cross-head-tools":
			await cycleAllCrossheadTools(machineId);
			return;
		case "cycle-long-head-tools":
			await cycleAllLongheadTools(machineId);
			return;
		case "cycle-track-tools":
			await cycleAllTrackTools(machineId);
			return;
		default:
			throw new Error(`Unsupported EM service command: ${command}`);
	}
};

export const useEmService = () => {
	const mutation = useMutation({ mutationFn: executeEmServiceCommand });

	return {
		toggleLongHeadCrease: (machineId: string, longHeadNumber: number, isActive: boolean) =>
			mutation.mutateAsync({
				machineId,
				command: "toggle-long-head-crease",
				toolNumber: longHeadNumber,
				isActive,
			}),
		toggleLongHeadLatch: (machineId: string, longHeadNumber: number, isActive: boolean) =>
			mutation.mutateAsync({
				machineId,
				command: "toggle-long-head-latch",
				toolNumber: longHeadNumber,
				isActive,
			}),
		toggleLongHeadCut: (machineId: string, longHeadNumber: number, isActive: boolean) =>
			mutation.mutateAsync({
				machineId,
				command: "toggle-long-head-cut",
				toolNumber: longHeadNumber,
				isActive,
			}),
		toggleTrackBrake: (machineId: string, trackNumber: number, isActive: boolean) =>
			mutation.mutateAsync({
				machineId,
				command: "toggle-track-brake",
				toolNumber: trackNumber,
				isActive,
			}),
		toggleTrackRoller: (machineId: string, trackNumber: number, isActive: boolean) =>
			mutation.mutateAsync({
				machineId,
				command: "toggle-track-roller",
				toolNumber: trackNumber,
				isActive,
			}),
		toggleCrossHeadCrease: (machineId: string, isActive: boolean) =>
			mutation.mutateAsync({ machineId, command: "toggle-cross-head-crease", isActive }),
		toggleCrossHeadLatch: (machineId: string, isActive: boolean) =>
			mutation.mutateAsync({ machineId, command: "toggle-cross-head-latch", isActive }),
		toggleCrossHeadCut: (machineId: string, isActive: boolean) =>
			mutation.mutateAsync({ machineId, command: "toggle-cross-head-cut", isActive }),
		cycleCrossHeadTools: (machineId: string) => mutation.mutateAsync({ machineId, command: "cycle-cross-head-tools" }),
		cycleLongHeadTools: (machineId: string) => mutation.mutateAsync({ machineId, command: "cycle-long-head-tools" }),
		cycleTrackTools: (machineId: string) => mutation.mutateAsync({ machineId, command: "cycle-track-tools" }),
	};
};
